import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ENGINE_LABELS, type Engine } from "@velivo/shared";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError } from "../api/client";
import { DataTable, type Column } from "../components/DataTable";
import { Tabs } from "../components/Tabs";
import { SidePanel } from "../components/SidePanel";
import { Icon } from "../components/Icon";
import "./dashboard.css";
import "./drift.css";
import "./actions.css";

/**
 * PRODUCT_FLOW.md Stage 6 — Action & Intervention.
 *
 * Deliberately a lightweight tracker, per Stage 6 §3: owner, status, due date,
 * drafts. The thing that makes it worth having over a spreadsheet is the
 * verification loop — resolving an item re-runs the prompt and reports whether
 * the drift actually closed — so that outcome is a first-class column rather
 * than something you have to open an item to discover.
 */

type Status = "open" | "in_progress" | "resolved" | "monitoring";
type Category =
  "media_outreach" | "owned_schema_fix" | "publisher_correction" | "escalation";
type RetestOutcome =
  "pending" | "confirmed_resolved" | "still_drifting" | "inconclusive";

interface ActionItem {
  actionItemId: string;
  driftFlagIds: string[];
  category: Category;
  title: string;
  rationale: string | null;
  status: Status;
  owner: string | null;
  dueDate: string | null;
  retestOutcome: RetestOutcome | null;
  ndiAtCreation: number | null;
  ndiAtResolution: number | null;
  createdAt: string | null;
  resolvedAt: string | null;
}

interface Draft {
  draftId: string;
  type: "outreach_email" | "jsonld_schema" | "correction_letter";
  subject: string | null;
  body: string;
  edited: boolean;
}

interface Recommendation {
  category: Category;
  title: string;
  rationale: string;
  targetSource: string | null;
  draftTypes: Draft["type"][];
}

const CATEGORY_LABELS: Record<Category, string> = {
  media_outreach: "Earned media outreach",
  owned_schema_fix: "Owned data & schema fix",
  publisher_correction: "Publisher correction",
  escalation: "Escalation",
};

const STATUS_LABELS: Record<Status, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  monitoring: "Monitoring",
};

const OUTCOME_LABELS: Record<RetestOutcome, string> = {
  pending: "Verifying…",
  confirmed_resolved: "Confirmed resolved",
  still_drifting: "Still drifting",
  inconclusive: "Inconclusive",
};

const OUTCOME_BADGE: Record<RetestOutcome, string> = {
  pending: "badge--info",
  confirmed_resolved: "badge--aligned",
  still_drifting: "badge--critical",
  inconclusive: "badge--neutral",
};

const DRAFT_LABELS: Record<Draft["type"], string> = {
  outreach_email: "Outreach email",
  correction_letter: "Correction letter",
  jsonld_schema: "JSON-LD snippet",
};

export function ActionsPage() {
  const { activeBrand, can } = useAuth();
  const brandId = activeBrand?.brandId ?? null;
  const [items, setItems] = useState<ActionItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [params] = useSearchParams();

  const mayManage = can("brand:manage_action_items");

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      const data = await api<{ items: ActionItem[] }>(
        `/brands/${brandId}/action-items`,
      );
      setItems(data.items);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Could not load action items.",
      );
      setItems([]);
    }
  }, [brandId]);

  useEffect(() => {
    void load();
  }, [load]);

  // A resolution kicks off a verification re-run; refresh while any is pending
  // rather than making the user reload to find out whether it worked.
  useEffect(() => {
    if (!items?.some((i) => i.retestOutcome === "pending")) return;
    const t = setTimeout(async () => {
      for (const item of items.filter((i) => i.retestOutcome === "pending")) {
        await api(`/action-items/${item.actionItemId}/settle-retest`, {
          method: "POST",
          body: {},
        }).catch(() => undefined);
      }
      void load();
    }, 4000);
    return () => clearTimeout(t);
  }, [items, load]);

  useEffect(() => {
    const focus = params.get("actionItemId");
    if (focus) setOpenId(focus);
  }, [params]);

  const columns: Column<ActionItem>[] = useMemo(
    () => [
      {
        key: "title",
        header: "Action",
        value: (r) => r.title,
        render: (r) => (
          <>
            <div className="action__title">{r.title}</div>
            <div className="muted">{CATEGORY_LABELS[r.category]}</div>
          </>
        ),
      },
      {
        key: "status",
        header: "Status",
        width: "130px",
        value: (r) => r.status,
        render: (r) => (
          <span className={`badge badge--status-${r.status}`}>
            {STATUS_LABELS[r.status]}
          </span>
        ),
      },
      {
        key: "verification",
        header: "Verification",
        width: "160px",
        value: (r) => r.retestOutcome ?? "",
        render: (r) =>
          r.retestOutcome ? (
            <span className={`badge ${OUTCOME_BADGE[r.retestOutcome]}`}>
              {OUTCOME_LABELS[r.retestOutcome]}
            </span>
          ) : (
            // Not blank — "not verified yet" is different from "verified and
            // fine", and this column is the product's whole argument.
            <span className="muted">not yet resolved</span>
          ),
      },
      {
        key: "impact",
        header: "NDI",
        width: "120px",
        align: "right",
        value: (r) => r.ndiAtCreation ?? 0,
        render: (r) => (
          <span className="numeric">
            {r.ndiAtCreation ?? "—"}
            {r.ndiAtResolution !== null ? (
              <>
                {" → "}
                <strong
                  className={
                    r.ndiAtCreation !== null &&
                    r.ndiAtResolution < r.ndiAtCreation
                      ? "numeric--good"
                      : "numeric--critical"
                  }
                >
                  {r.ndiAtResolution}
                </strong>
              </>
            ) : null}
          </span>
        ),
      },
      {
        key: "due",
        header: "Due",
        width: "120px",
        value: (r) => r.dueDate ?? "",
        render: (r) =>
          r.dueDate ? new Date(r.dueDate).toLocaleDateString() : "—",
      },
      {
        key: "open",
        header: "",
        width: "110px",
        render: (r) => (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setOpenId(r.actionItemId)}
          >
            Open <Icon name="arrowRight" />
          </button>
        ),
      },
    ],
    [],
  );

  const byStatus = (status: Status[]) =>
    (items ?? []).filter((i) => status.includes(i.status));

  return (
    <div className="stack">
      <header className="page__header">
        <h1 className="page__title">Actions</h1>
        <p className="page__subtitle">
          Every flagged drift becomes an assignable intervention. Resolving one
          re-runs the prompt automatically and tells you whether the drift
          actually closed.
        </p>
      </header>

      {error ? <div className="alert alert--error">{error}</div> : null}

      <Tabs
        ariaLabel="Action sections"
        tabs={[
          {
            id: "active",
            label: "Active",
            icon: "actions",
            badge: byStatus(["open", "in_progress"]).length || null,
            render: () => (
              <DataTable
                rows={items ? byStatus(["open", "in_progress"]) : null}
                columns={columns}
                rowKey={(r) => r.actionItemId}
                searchPlaceholder="Search actions…"
                empty={{
                  icon: "check",
                  title: "No open actions",
                  description:
                    "Open a drift finding and take action on it — the recommendation is built from that finding’s own evidence, not a template.",
                }}
              />
            ),
          },
          {
            id: "resolved",
            label: "Resolved",
            icon: "check",
            render: () => (
              <DataTable
                rows={items ? byStatus(["resolved", "monitoring"]) : null}
                columns={columns}
                rowKey={(r) => r.actionItemId}
                searchPlaceholder="Search resolved actions…"
                empty={{
                  icon: "clock",
                  title: "Nothing resolved yet",
                  description:
                    "Resolved actions land here with the verification result — whether the re-run confirmed the drift closed.",
                }}
              />
            ),
          },
        ]}
      />

      {openId ? (
        <ActionPanel
          actionItemId={openId}
          mayManage={mayManage}
          onClose={() => setOpenId(null)}
          onChanged={load}
        />
      ) : null}
    </div>
  );
}

function ActionPanel({
  actionItemId,
  mayManage,
  onClose,
  onChanged,
}: {
  actionItemId: string;
  mayManage: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<
    (ActionItem & { drafts: Draft[] }) | null
  >(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDetail(await api(`/action-items/${actionItemId}`));
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Could not load this action.",
      );
    }
  }, [actionItemId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(label: string, fn: () => Promise<unknown>) {
    setBusy(label);
    setError(null);
    try {
      await fn();
      await load();
      onChanged();
    } catch (err) {
      setError(
        err instanceof ApiRequestError ? err.message : "That did not work.",
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <SidePanel
      open
      title={detail?.title ?? "Action"}
      subtitle={
        detail ? (
          <>
            <span className={`badge badge--status-${detail.status}`}>
              {STATUS_LABELS[detail.status]}
            </span>
            <span className="badge badge--neutral">
              {CATEGORY_LABELS[detail.category]}
            </span>
            {detail.retestOutcome ? (
              <span className={`badge ${OUTCOME_BADGE[detail.retestOutcome]}`}>
                {OUTCOME_LABELS[detail.retestOutcome]}
              </span>
            ) : null}
          </>
        ) : null
      }
      onClose={onClose}
      footer={
        detail && mayManage && detail.status !== "resolved" ? (
          <>
            <button
              type="button"
              className="btn btn--secondary"
              disabled={busy !== null}
              onClick={() =>
                void act("progress", () =>
                  api(`/action-items/${actionItemId}`, {
                    method: "PATCH",
                    body: { status: "in_progress" },
                  }),
                )
              }
            >
              Mark in progress
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy !== null}
              onClick={() =>
                void act("resolve", async () => {
                  const res = await api<{ message: string }>(
                    `/action-items/${actionItemId}/resolve`,
                    { method: "POST", body: {} },
                  );
                  setNote(res.message);
                })
              }
            >
              {busy === "resolve" ? "Resolving…" : "Mark resolved & verify"}
            </button>
          </>
        ) : null
      }
    >
      {error ? <div className="alert alert--error">{error}</div> : null}
      {note ? <div className="alert alert--info">{note}</div> : null}

      {!detail ? (
        <div className="skeleton-stack">
          <div className="skeleton" />
          <div className="skeleton" />
        </div>
      ) : (
        <Tabs
          urlSync={false}
          compact
          ariaLabel="Action detail"
          tabs={[
            {
              id: "why",
              label: "Why & impact",
              icon: "drift",
              render: () => (
                <>
                  {detail.rationale ? (
                    <section className="evidence">
                      <h3 className="evidence__label">Why this action</h3>
                      <p className="evidence__rationale">{detail.rationale}</p>
                    </section>
                  ) : null}

                  {detail.ndiAtCreation !== null ? (
                    <section className="evidence">
                      <h3 className="evidence__label">Impact</h3>
                      <p className="impact">
                        <span className="impact__value">
                          {detail.ndiAtCreation}
                        </span>
                        <Icon name="arrowRight" />
                        <span className="impact__value">
                          {detail.ndiAtResolution ?? (
                            <span className="muted">pending</span>
                          )}
                        </span>
                        <span className="muted">
                          Narrative Drift Index on the affected prompt
                        </span>
                      </p>
                      {detail.retestOutcome ? (
                        <p className="muted">
                          Engines change their answers for reasons outside your
                          control, so treat this as evidence the drift closed,
                          not proof of what closed it.
                        </p>
                      ) : null}
                    </section>
                  ) : (
                    <p className="muted">
                      No drift score was recorded for the linked findings, so
                      there is no before/after to show.
                    </p>
                  )}
                </>
              ),
            },
            {
              id: "drafts",
              label: "Drafts",
              icon: "mail",
              badge: detail.drafts.length || null,
              render: () => (
                <section className="evidence">
                  {mayManage ? (
                    <div className="draftbar">
                      {(Object.keys(DRAFT_LABELS) as Draft["type"][]).map(
                        (type) => (
                          <button
                            key={type}
                            type="button"
                            className="btn btn--secondary btn--sm"
                            disabled={busy !== null}
                            onClick={() =>
                              void act(type, () =>
                                api(`/action-items/${actionItemId}/drafts`, {
                                  method: "POST",
                                  body: { type },
                                }),
                              )
                            }
                          >
                            {busy === type
                              ? "Generating…"
                              : `Generate ${DRAFT_LABELS[type]}`}
                          </button>
                        ),
                      )}
                    </div>
                  ) : null}

                  {detail.drafts.length === 0 ? (
                    <p className="muted">
                      No drafts yet. A generated draft is a starting point
                      grounded in this finding&rsquo;s evidence — edit it before
                      you use it.
                    </p>
                  ) : (
                    <ul className="drafts">
                      {detail.drafts.map((draft) => (
                        <DraftCard
                          key={draft.draftId}
                          draft={draft}
                          mayManage={mayManage}
                          onSaved={load}
                        />
                      ))}
                    </ul>
                  )}
                </section>
              ),
            },
          ]}
        />
      )}
    </SidePanel>
  );
}

function DraftCard({
  draft,
  mayManage,
  onSaved,
}: {
  draft: Draft;
  mayManage: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(draft.body);
  const [saving, setSaving] = useState(false);

  return (
    <li className="draft">
      <div className="draft__head">
        <span className="badge badge--neutral">{DRAFT_LABELS[draft.type]}</span>
        {draft.edited ? (
          <span className="badge badge--info">Edited</span>
        ) : null}
        {mayManage ? (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? "Cancel" : "Edit"}
          </button>
        ) : null}
      </div>
      {draft.subject ? (
        <div className="draft__subject">{draft.subject}</div>
      ) : null}
      {editing ? (
        <>
          <textarea
            className="draft__editor"
            value={text}
            rows={10}
            onChange={(e) => setText(e.target.value)}
          />
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              await api(`/action-drafts/${draft.draftId}`, {
                method: "PATCH",
                body: { body: text },
              }).catch(() => undefined);
              setSaving(false);
              setEditing(false);
              onSaved();
            }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      ) : (
        <pre className="draft__body">{draft.body}</pre>
      )}
    </li>
  );
}

/** Exported so the Drift screen can offer "take action" without duplicating it. */
export async function createActionFromRecommendation(
  flagId: string,
  recommendation: Recommendation,
): Promise<{ actionItemId: string }> {
  return api(`/drift-flags/${flagId}/action-items`, {
    method: "POST",
    body: {
      category: recommendation.category,
      title: recommendation.title,
      rationale: recommendation.rationale,
    },
  });
}

export type { Recommendation, Engine };
export { ENGINE_LABELS };
